import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert, CircularProgress, Stack, Chip, Box } from '@mui/material';
import { aprovadorApi, PanelItem, AprovadorError, brl } from '@/services/aprovador';

interface Props {
  token: string;
  /** Um ou vários pedidos (rejeição em lote usa o mesmo motivo). */
  items: PanelItem[] | null;
  onClose: () => void;
  onDone: () => void;
  onSessionLost: () => void;
}

const SUGESTOES = ['Título cancelado', 'Já foi pago', 'Duplicado', 'Valor errado', 'Aguardar outra data'];

const RejeitarDialog: React.FC<Props> = ({ token, items, onClose, onDone, onSessionLost }) => {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lista = items || [];
  const chave = lista.map((i) => i.id).join(',');

  useEffect(() => { setMotivo(''); setError(null); }, [chave]);

  const salvar = async () => {
    setSaving(true);
    setError(null);
    const falhas: string[] = [];
    for (const it of lista) {
      try {
        await aprovadorApi.rejeitar(token, it.id, motivo.trim());
      } catch (e) {
        const err = e as AprovadorError;
        if (err.status === 401) { setSaving(false); onSessionLost(); return; }
        falhas.push(`${it.beneficiaryName || brl(it.amount)}: ${err.message}`);
      }
    }
    setSaving(false);
    onDone();
    if (falhas.length) setError(falhas.join('\n'));
    else onClose();
  };

  const total = lista.reduce((s, i) => s + i.amount, 0);

  return (
    <Dialog open={lista.length > 0} onClose={() => !saving && onClose()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{lista.length > 1 ? `Rejeitar ${lista.length} pedidos` : 'Rejeitar pedido'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {lista.length === 1 ? (
            <Typography variant="body2">
              <b>{brl(lista[0].amount)}</b> — {lista[0].beneficiaryName || lista[0].description || 'sem favorecido'}
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
              {lista.map((i) => (
                <Typography key={i.id} variant="body2"><b>{brl(i.amount)}</b> — {i.beneficiaryName || i.description || 'sem favorecido'}</Typography>
              ))}
              <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>Total {brl(total)}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SUGESTOES.map((s) => <Chip key={s} label={s} size="small" onClick={() => setMotivo(s)} variant={motivo === s ? 'filled' : 'outlined'} />)}
          </Box>
          <TextField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} fullWidth multiline minRows={2} inputProps={{ maxLength: 500 }} />
          <Typography variant="caption" color="text.secondary">Sai da lista, e o motivo vai para o grupo e para o sistema de origem.</Typography>
          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Voltar</Button>
        <Button variant="contained" color="error" onClick={salvar} disabled={saving || motivo.trim().length < 3}>
          {saving ? <CircularProgress size={22} color="inherit" /> : lista.length > 1 ? `Rejeitar ${lista.length}` : 'Rejeitar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejeitarDialog;
